import { useEffect, useMemo, useState } from "react";
import { getTaskStatuses } from "@/api/tasks";
import type { TaskStatusConfig } from "@/api/types";

export interface UseTaskStatusesResult {
  /** Catalog as the backend ships it, sorted by `order`. Empty when the
   *  endpoint is unavailable / returns nothing — callers should treat
   *  this as "no dynamic statuses configured" and fall back to the
   *  legacy is_completed flow. */
  statuses: TaskStatusConfig[];
  /** True until the first fetch resolves (success or failure). Useful
   *  for skeleton states. */
  loading: boolean;
  /** Stable sets / lookups derived from the catalog. Memoised so
   *  consumers can use them as deps without re-render churn. */
  doneKeys: ReadonlySet<string>;
  initialKey: string | null;
  /** Lookup by key. Returns null when the catalog doesn't carry that
   *  key (renamed / deleted on the backend). */
  getStatusConfig: (key: string | null | undefined) => TaskStatusConfig | null;
  isDone: (key: string | null | undefined) => boolean;
  isInitial: (key: string | null | undefined) => boolean;
}

/**
 * Single source of truth for the task-status catalog. Fetched once per
 * project; all consumers (TasksPage, TaskKanbanBoard, TaskItem,
 * TaskDetailSheet) read from here so no surface ever hardcodes a key
 * like "done" or "todo" — those move with the operator's catalog
 * config.
 */
export function useTaskStatuses(slug: string | null): UseTaskStatusesResult {
  const [statuses, setStatuses] = useState<TaskStatusConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(!!slug);

  useEffect(() => {
    if (!slug) {
      setStatuses([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTaskStatuses(slug)
      .then((s) => {
        if (cancelled) return;
        setStatuses(s.slice().sort((a, b) => a.order - b.order));
      })
      .catch(() => {
        if (cancelled) return;
        setStatuses([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const doneKeys = useMemo<ReadonlySet<string>>(
    () => new Set(statuses.filter((s) => s.is_done).map((s) => s.key)),
    [statuses],
  );

  const initialKey = useMemo<string | null>(
    () => statuses.find((s) => s.is_initial)?.key ?? null,
    [statuses],
  );

  const byKey = useMemo<Map<string, TaskStatusConfig>>(() => {
    const m = new Map<string, TaskStatusConfig>();
    for (const s of statuses) m.set(s.key, s);
    return m;
  }, [statuses]);

  return {
    statuses,
    loading,
    doneKeys,
    initialKey,
    getStatusConfig: (key) => (key ? byKey.get(key) ?? null : null),
    isDone: (key) => !!key && doneKeys.has(key),
    isInitial: (key) => !!key && key === initialKey,
  };
}
